import React from "react";
import { Globe, Copy, Trash2, Lock, ExternalLink, Loader2, Settings } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

function PublishDomainTab({
    workflow, publicUrl, isGeneratingUrl, hasActiveSubscription,
    onGenerateUrl, onRemoveUrl, onGoToDomains,
}) {
    const { t } = useTranslation();
    const isAppV2 = workflow.workflowType === "APP_V2";
    const displayDomain = workflow.customDomain || workflow.internalDomain;

    const copyText = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(t("publishModal.publicPublishing.copySuccess"));
    };

    if (isAppV2) {
        return (
            <div className="space-y-4">
                <p className="text-xs text-muted-foreground leading-relaxed">
                    {t("publishModal.domain.description")}
                </p>

                {displayDomain ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                                {t("publishModal.publicPublishing.appDomain")}
                            </span>
                            <div className="flex items-center gap-0.5">
                                <Button variant="ghost" size="icon" onClick={() => copyText(`https://${displayDomain}`)} className="h-7 w-7">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => window.open(`https://${displayDomain}`, "_blank", "noopener,noreferrer")} className="h-7 w-7">
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                        <div className="bg-muted/50 border rounded-md px-3 py-2.5">
                            <code className="text-sm text-foreground break-all">{`https://${displayDomain}`}</code>
                        </div>
                    </div>
                ) : (
                    <div className="py-4 text-center space-y-2">
                        <Globe className="w-8 h-8 text-muted-foreground/30 mx-auto" />
                        <p className="text-sm font-medium text-muted-foreground">{t("publishModal.publicPublishing.noDomain")}</p>
                    </div>
                )}

                <button
                    onClick={onGoToDomains}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md border border-dashed border-muted-foreground/25 hover:border-primary/40 hover:bg-muted/50 transition-colors text-left group"
                >
                    <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                        <Settings className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium">{t("publishModal.domain.addCustomDomain")}</p>
                        <p className="text-xs text-muted-foreground">{t("publishModal.domain.addCustomDomainHint")}</p>
                    </div>
                </button>
            </div>
        );
    }

    if (isGeneratingUrl) {
        return (
            <div className="flex items-center justify-center py-12 gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t("publishModal.publicPublishing.generatingUrl")}</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
                {t("publishModal.publicPublishing.description")}
            </p>

            {publicUrl ? (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {t("publishModal.publicPublishing.publicUrl")}
                        </span>
                        <div className="flex items-center gap-0.5">
                            <Button variant="ghost" size="icon" onClick={() => copyText(publicUrl)} className="h-7 w-7">
                                <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={onRemoveUrl} className="h-7 w-7 text-red-500 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                    <div className="bg-muted/50 border rounded-md px-3 py-2.5">
                        <code className="text-sm text-foreground break-all">{publicUrl}</code>
                    </div>
                </div>
            ) : (
                <>
                    {!hasActiveSubscription && (
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                            <div className="flex items-center gap-2">
                                <Lock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                    {t("publishModal.publicPublishing.requiresSubscription")}
                                </span>
                            </div>
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1.5 ml-5.5">
                                {t("publishModal.publicPublishing.upgradeMessage")}
                            </p>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{t("publishModal.publicPublishing.noUrlGenerated")}</span>
                        <Button variant="outline" size="sm" onClick={onGenerateUrl} disabled={!hasActiveSubscription}>
                            {!hasActiveSubscription && <Lock className="w-3.5 h-3.5 mr-1.5" />}
                            {t("publishModal.publicPublishing.generateUrl")}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

export default React.memo(PublishDomainTab);
